import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { History, Calendar as CalendarIcon, User, Activity, Search, Download, Filter } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface AttendanceRecord {
  id: string;
  date: string;
  time: string;
  status: string;
  notes?: string;
  created_at: string;
  student: {
    id: string;
    name: string;
    class: string;
  };
  activity: {
    id: string;
    name: string;
  };
}

const attendanceStatuses = [
  { value: 'all', label: 'Todos los estados', color: 'bg-muted' },
  { value: 'present', label: 'Presente', color: 'bg-green-500' },
  { value: 'absent', label: 'Ausente', color: 'bg-red-500' },
  { value: 'late', label: 'Tardanza', color: 'bg-orange-500' },
  { value: 'justified', label: 'Justificado', color: 'bg-blue-500' }
];

export const AttendanceHistory = () => {
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedActivity, setSelectedActivity] = useState("all");
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [activities, setActivities] = useState<any[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    loadAttendanceHistory();
    loadActivities();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [records, searchTerm, selectedStatus, selectedActivity, selectedDate]);

  // Exponer función de recarga
  useEffect(() => {
    (window as any).refreshAttendanceHistory = loadAttendanceHistory;
    return () => {
      delete (window as any).refreshAttendanceHistory;
    };
  }, []);

  const loadActivities = async () => {
    try {
      const { data, error } = await supabase
        .from('activities')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      setActivities(data || []);
    } catch (error) {
      console.error('Error loading activities:', error);
    }
  };

  const loadAttendanceHistory = async () => {
    try {
      setLoading(true);
      
      // Consultar los últimos 30 días de asistencia
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const fromDate = format(thirtyDaysAgo, 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('attendances')
        .select(`
          id,
          date,
          time,
          status,
          notes,
          student_id,
          activity_id,
          created_at
        `)
        .gte('date', fromDate)
        .order('date', { ascending: false })
        .order('time', { ascending: false });

      if (error) throw error;

      // Obtener datos de estudiantes y actividades por separado
      const studentIds = [...new Set(data?.map(record => record.student_id) || [])];
      const activityIds = [...new Set(data?.map(record => record.activity_id) || [])];

      const [studentsResult, activitiesResult] = await Promise.all([
        supabase.from('students').select('id, name, class').in('id', studentIds),
        supabase.from('activities').select('id, name').in('id', activityIds)
      ]);

      if (studentsResult.error) throw studentsResult.error;
      if (activitiesResult.error) throw activitiesResult.error;

      // Crear mapas para lookup rápido
      const studentsMap = new Map(studentsResult.data?.map(s => [s.id, s]) || []);
      const activitiesMap = new Map(activitiesResult.data?.map(a => [a.id, a]) || []);

      // Transformar los registros
      const transformedRecords: AttendanceRecord[] = data?.map(record => {
        const student = studentsMap.get(record.student_id);
        const activity = activitiesMap.get(record.activity_id);
        
        return {
          id: record.id,
          date: record.date,
          time: record.time,
          status: record.status,
          notes: record.notes,
          created_at: record.created_at,
          student: {
            id: student?.id || '',
            name: student?.name || 'Estudiante desconocido',
            class: student?.class || ''
          },
          activity: {
            id: activity?.id || '',
            name: activity?.name || 'Actividad desconocida'
          }
        };
      }).filter(record => record.student.id && record.activity.id) || [];

      setRecords(transformedRecords);
    } catch (error) {
      console.error('Error loading attendance history:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar el historial de asistencia",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = records;

    // Filtro por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(record =>
        record.student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.student.class.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.activity.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por estado
    if (selectedStatus !== "all") {
      filtered = filtered.filter(record => record.status === selectedStatus);
    }

    // Filtro por actividad
    if (selectedActivity !== "all") {
      filtered = filtered.filter(record => record.activity.id === selectedActivity);
    }

    // Filtro por fecha
    if (selectedDate) {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      filtered = filtered.filter(record => record.date === formattedDate);
    }

    setFilteredRecords(filtered);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = attendanceStatuses.find(s => s.value === status);
    if (!statusConfig) return null;

    return (
      <Badge variant="secondary" className={`${statusConfig.color} text-white`}>
        {statusConfig.label}
      </Badge>
    );
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Fecha', 'Hora', 'Estudiante', 'Clase', 'Actividad', 'Estado', 'Notas'],
      ...filteredRecords.map(record => [
        record.date,
        record.time,
        record.student.name,
        record.student.class,
        record.activity.name,
        attendanceStatuses.find(s => s.value === record.status)?.label || record.status,
        record.notes || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `asistencia_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: "Exportación completada",
      description: "El archivo CSV ha sido descargado"
    });
  };

  return (
    <Card className="shadow-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Historial de Asistencia - Últimos 30 días ({filteredRecords.length})
          </CardTitle>
          <div className="flex gap-2">
            <Button onClick={loadAttendanceHistory} variant="outline" size="sm">
              <History className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            <Button onClick={exportToCSV} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-muted rounded-lg">
          <div>
            <label className="block text-sm font-medium mb-1">Buscar</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Estudiante, clase o actividad..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Estado</label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background border z-50">
                {attendanceStatuses.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Actividad</label>
            <Select value={selectedActivity} onValueChange={setSelectedActivity}>
              <SelectTrigger className="bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background border z-50">
                <SelectItem value="all">Todas las actividades</SelectItem>
                {activities.map((activity) => (
                  <SelectItem key={activity.id} value={activity.id}>
                    {activity.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Fecha</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, "dd/MM/yyyy", { locale: es }) : "Seleccionar fecha"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-background border z-50">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  locale={es}
                />
                {selectedDate && (
                  <div className="p-2 border-t">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setSelectedDate(undefined)}
                      className="w-full"
                    >
                      Limpiar filtro
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Lista de registros */}
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">
            Cargando historial...
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">No hay registros</p>
            <p className="text-sm">
              {records.length === 0 ? "No se encontraron registros de asistencia" : "Intenta ajustar los filtros"}
            </p>
          </div>
        ) : (
          <ScrollArea className="h-96">
            <div className="space-y-3">
              {filteredRecords.map((record) => (
                <Card key={record.id} className="border hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex flex-col items-center text-center min-w-[120px]">
                          <div className="text-sm font-medium">
                            {format(new Date(record.date), 'dd MMM', { locale: es })}
                          </div>
                          <div className="text-sm font-bold text-primary bg-primary/10 px-2 py-1 rounded-md">
                            {record.time.substring(0, 5)}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Registro: {format(new Date(record.created_at), 'HH:mm:ss')}
                          </div>
                          <div className="text-xs text-success font-medium">
                            Timestamp preciso
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <User className="w-4 h-4 text-primary" />
                            <span className="font-medium">{record.student.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {record.student.class}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Activity className="w-4 h-4" />
                            <span>{record.activity.name}</span>
                          </div>
                          {record.notes && (
                            <p className="text-xs text-muted-foreground mt-1 italic">
                              "{record.notes}"
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        {getStatusBadge(record.status)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};